import { PortalReferralDisplayStatus } from '@app/services/api/api.types';

import { REFERRAL_DETAIL_MODAL_COPY } from './referral-detail-modal.copy';

export type ReferralTimelineStepState = 'done' | 'active' | 'pending' | 'failed' | 'expired';

export type ReferralTimelineStep = {
  key: string;
  label: string;
  description: string;
  state: ReferralTimelineStepState;
};

const { steps: copy } = REFERRAL_DETAIL_MODAL_COPY;

const successPath: { key: string; label: string; description: string }[] = [
  { key: 'submitted', ...copy.submitted },
  { key: 'contacted', ...copy.contacted },
  { key: 'in_progress', ...copy.inProgress },
  { key: 'booked', ...copy.booked },
  { key: 'journey_completed', ...copy.journeyCompleted },
];

function mapPath(
  activeIndex: number,
  terminal?: {
    index: number;
    label: string;
    description: string;
    state?: ReferralTimelineStepState;
  },
): ReferralTimelineStep[] {
  const steps: ReferralTimelineStep[] = successPath.map((step, index) => {
    if (terminal && index === terminal.index) {
      return {
        ...step,
        label: terminal.label,
        description: terminal.description,
        state: (terminal.state ?? 'failed') as ReferralTimelineStepState,
      };
    }

    if (index < activeIndex) {
      return { ...step, state: 'done' as ReferralTimelineStepState };
    }

    if (index === activeIndex) {
      return { ...step, state: 'active' as ReferralTimelineStepState };
    }

    return { ...step, state: 'pending' as ReferralTimelineStepState };
  });

  if (terminal && terminal.index > successPath.length - 1) {
    steps.push({
      key: 'terminal',
      label: terminal.label,
      description: terminal.description,
      state: (terminal.state ?? 'failed') as ReferralTimelineStepState,
    });
  }

  return steps.filter((_, index) => !terminal || index <= Math.max(activeIndex, terminal.index));
}

export function buildReferralTimeline(displayStatus: PortalReferralDisplayStatus) {
  switch (displayStatus) {
    case PortalReferralDisplayStatus.REFERRAL_SUBMITTED:
      return mapPath(0);
    case PortalReferralDisplayStatus.CONTACTED:
      return mapPath(1);
    case PortalReferralDisplayStatus.IN_PROGRESS:
      return mapPath(2);
    case PortalReferralDisplayStatus.BOOKED:
      return mapPath(3);
    case PortalReferralDisplayStatus.JOURNEY_COMPLETED:
      return mapPath(4);
    case PortalReferralDisplayStatus.SUCCESS:
      return [
        ...successPath.slice(0, 4).map((step) => ({
          ...step,
          state: 'done' as ReferralTimelineStepState,
        })),
        {
          key: 'success',
          ...copy.success,
          state: 'done' as ReferralTimelineStepState,
        },
      ];
    case PortalReferralDisplayStatus.INELIGIBLE:
      return mapPath(2, {
        index: 2,
        ...copy.notEligible,
        state: 'failed',
      });
    case PortalReferralDisplayStatus.EXPIRED:
      return [
        ...successPath.slice(0, 3).map((step) => ({
          ...step,
          state: 'done' as ReferralTimelineStepState,
        })),
        {
          key: 'expired',
          ...copy.expired,
          state: 'expired' as ReferralTimelineStepState,
        },
      ];
    case PortalReferralDisplayStatus.REJECTED:
      return [
        ...successPath.slice(0, 2).map((step) => ({
          ...step,
          state: 'done' as ReferralTimelineStepState,
        })),
        {
          key: 'rejected',
          ...copy.rejected,
          state: 'failed' as ReferralTimelineStepState,
        },
      ];
    default:
      return mapPath(0);
  }
}

export function referralInitials(maskedName: string) {
  const parts = maskedName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return '?';
  }

  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

  return `${first}${last}`.toUpperCase();
}

export function refereeWindowMonths(days: number) {
  if (days >= 85 && days <= 95) {
    return 3;
  }

  return Math.max(1, Math.round(days / 30));
}
